import { SecureInputsGlobalPostMessageEventKind } from "@highnoteplatform/types";
import type {
  SecureInputsTokenizationError,
  HighnotePostMessageEvent,
  SecureInputsErrorCallback,
  SecureInputsFieldInputError,
  SecureInputsGlobalPostMessageEvent,
  SecureInputsSuccessCallback,
  SecureInputsTokenizationSuccessCallback,
  SecureInputsOnStateChangeCallback,
  SecureInputsInputCallback,
  SecureInputsTokenizationErrorCallback,
} from "@highnoteplatform/types";

import { iframeOrigin } from "./config";

const FALLBACK_ERROR_MESSAGE =
  "Something went wrong. Please try again or contact support.";

interface HandlePostMessageParameters {
  uuid: string;
  onInput?: SecureInputsInputCallback;
  onPinEntryError?: SecureInputsErrorCallback;
  onPinEntrySuccess?: SecureInputsSuccessCallback;
  onReady?: () => void;
  onStateChange?: SecureInputsOnStateChangeCallback;
  onTokenizeCardError?: SecureInputsTokenizationErrorCallback;
  onTokenizeCardSuccess?: SecureInputsTokenizationSuccessCallback;
}

const createRouteError =
  (
    onPinEntryError?: SecureInputsErrorCallback,
    onTokenizeCardError?: SecureInputsTokenizationErrorCallback
  ) =>
  (error: SecureInputsTokenizationError) => {
    onTokenizeCardError?.(error);
    onPinEntryError?.(error);
  };

export const handlePostMessage =
  ({
    uuid,
    onPinEntryError,
    onInput,
    onPinEntrySuccess,
    onTokenizeCardSuccess,
    onStateChange,
    onReady,
    onTokenizeCardError,
  }: HandlePostMessageParameters) =>
  (
    event: MessageEvent<
      HighnotePostMessageEvent<SecureInputsGlobalPostMessageEvent>
    >
  ): boolean => {
    if (event.origin !== iframeOrigin || uuid !== event.data.uuid) {
      return false;
    }

    const { data } = event.data;
    const routeError = createRouteError(onPinEntryError, onTokenizeCardError);

    switch (data.__typename) {
      case SecureInputsGlobalPostMessageEventKind.INVALID_CREDENTIAL_ERROR:
        routeError({
          name: "InvalidCredentialError",
          context: data.context,
        });

        break;
      case SecureInputsGlobalPostMessageEventKind.ON_PIN_CHANGE_SUCCESS:
        onPinEntrySuccess?.(data.field);

        break;
      case SecureInputsGlobalPostMessageEventKind.ON_TOKENIZE_CARD_SUCCESS:
        onTokenizeCardSuccess?.(data.paymentMethodToken);

        break;
      case SecureInputsGlobalPostMessageEventKind.ON_STATE_CHANGE:
        onStateChange?.(data.field, data.state);

        break;
      case SecureInputsGlobalPostMessageEventKind.ON_INPUT:
        onInput?.(data.field, data.config);

        break;
      case SecureInputsGlobalPostMessageEventKind.ON_READY:
        onReady?.();

        break;
      case SecureInputsGlobalPostMessageEventKind.USER_INPUT_ERROR: {
        const { field, message } = data;
        const error: SecureInputsFieldInputError = {
          name: "SecureInputsFieldInputError",
          field,
          message,
        };

        onPinEntryError?.(error);

        break;
      }
      case SecureInputsGlobalPostMessageEventKind.REQUEST_ERROR: {
        const { field, message, context } = data;

        routeError({
          name: "SecureInputsRequestError",
          field,
          message,
          context,
        });

        break;
      }
      case SecureInputsGlobalPostMessageEventKind.GENERIC_ERROR:
        routeError({
          name: "Error",
          message: data.message,
        });

        break;

      default:
        routeError({
          name: "Error",
          message: FALLBACK_ERROR_MESSAGE,
        });

        break;
    }

    return true;
  };
